import { DateTrunc } from '@tableau/metrics-engine';
import { MetricsBaseProps } from '@tableau/metrics-shared-widgets-dom/';
import { MetricDetailViewProps } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricDetailView';
import { MetricMetadataPanelProps } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricMetadataPanel';
import { DataPoint } from '@tableau/metrics-shared-widgets-dom/lib/src/models';
import { SpacesSiteUrls } from '../app/spaces/SpacesSiteUrls';
import { IMetricDataInfo, IMetricInfo } from '../ts/models/IMetricInfo';
import { DateGranularity, IMetricDataList, IMetricDataPoint, IMetricDataPoints } from '../ts/ServerApi/types/IMetricDataList';

export const metricCardProps = (metric: IMetricDataInfo): MetricsBaseProps => {
  const data = metric.dataPoints ? metric.dataPoints : [];
  const measureFormat = metric.schema ? metric.schema.measure.format : '';
  return {
    testId: 'metric-card',
    data: data.slice(-100),
    lastUpdatedTimestamp: validDateOrUndefined(metric.dataRefreshedTimestamp),
    measureFormat,
  };
};

export const parseDataPoints = (points: IMetricDataPoints): ReadonlyArray<DataPoint> => {
  if (!points.points) {
    return [];
  }
  return points.points.map(parseDataPoint).filter((point) => point.timestamp !== undefined);
};

export const parseDataPoint = (point: IMetricDataPoint): DataPoint => {
  return {
    measure: point.measure || 0,
    timestamp: validDateOrUndefined(point.timestamp),
  };
};

export const getMergedMetricInfo = (metricInfo: IMetricInfo, metricDataList: IMetricDataList): IMetricInfo => {
  const data = metricDataList.data[metricInfo.id];

  if (!!data) {
    return { ...metricInfo,
      dataPoints: parseDataPoints(data),
      schema: data.schema,
    dataRefreshedTimestamp: data.dataRefreshedTimestamp };
  } else {
    return metricInfo;
  }
};

export const getMetricMeasure = (metric: IMetricDataInfo): number => {
  return metric && metric.dataPoints && metric.dataPoints.length > 0 && metric.dataPoints[metric.dataPoints.length - 1].measure;
};

export const getMeasureFormat = (metric: IMetricDataInfo): string | undefined => {
  return metric && metric.schema && metric.schema.measure.format;
};

export const metricDetailProps = (metric: IMetricInfo, urls: SpacesSiteUrls): MetricDetailViewProps => {
  const data = metric.dataPoints ? metric.dataPoints : [];
  const dateTrunc = metric.schema && metric.schema.timestamp ? dateTruncFromGranularity(metric.schema.timestamp.dateGranularity) : DateTrunc.UNKNOWN;

  return {
    metricName: metric.name,
    testId: 'metric-detail',
    data,
    dateTrunc,
    lastUpdatedTimestamp: validDateOrUndefined(metric.dataRefreshedTimestamp),
    metadataProps: getMetricMetadata(metric, urls),
    measureFormat: metric.schema ? metric.schema.measure.format : '',
  };
};

export const dateTruncFromGranularity = (date?: DateGranularity): DateTrunc => {
  switch (date) {
    case DateGranularity.SECOND: {
      return DateTrunc.SECOND;
    }
    case DateGranularity.MINUTE: {
      return DateTrunc.MINUTE;
    }
    case DateGranularity.HOUR: {
      return DateTrunc.HOUR;
    }
    case DateGranularity.DAY: {
      return DateTrunc.DAY;
    }
    case DateGranularity.ISO_WEEKDAY: {
      return DateTrunc.ISO_WEEKDAY;
    }
    case DateGranularity.WEEK: {
      return DateTrunc.WEEK;
    }
    case DateGranularity.ISO_WEEK: {
      return DateTrunc.ISO_WEEK;
    }
    case DateGranularity.MONTH: {
      return DateTrunc.MONTH;
    }
    case DateGranularity.QUARTER: {
      return DateTrunc.QUARTER;
    }
    case DateGranularity.ISO_QUARTER: {
      return DateTrunc.ISO_QTR;
    }
    case DateGranularity.YEAR: {
      return DateTrunc.YEAR;
    }
    case DateGranularity.ISO_YEAR: {
      return DateTrunc.ISO_YEAR;
    }
    case DateGranularity.UNKNOWN:
    case undefined:
    default: {
      return DateTrunc.UNKNOWN;
    }
  }
};

const getMetricMetadata = (metric: IMetricInfo, urls: SpacesSiteUrls): MetricMetadataPanelProps => {
  const metadataProps: MetricMetadataPanelProps = {
    description: metric.description,
    tags: metric.tags,
    modifiedTimestamp: metric.updatedAt,
  };

  if (metric.owner) {
    metadataProps.ownerName = metric.owner.displayName;
    metadataProps.ownerUrl = urls.userContentUrl(metric.owner.username, metric.owner.domainName);
    metadataProps.isHiddenInternalUser = metric.owner.isHiddenInternalUser;
  }

  if (metric.connectedView) {
    metadataProps.connectedViewtitle = metric.connectedView.name;
    metadataProps.connectedViewThumbnailUrl = metric.connectedView.thumbnailUrl;
    metadataProps.connectedViewUrl = urls.viewUrl(metric.connectedView.path);
  }

  if (metric.schema) {
    metadataProps.schemaMeasure = metric.schema.measure.displayName;
    metadataProps.schemaTimestamp = metric.schema.timestamp ? metric.schema.timestamp.displayName : null;
  }

  if (metric.project) {
     metadataProps.projectName = metric.project.name;
     metadataProps.projectUrl = urls.projectUrl(metric.project.id);
  }

  return metadataProps;
};

const timezoneOffsetRegex = /[+-]\d\d:?(\d\d)?$/;

// This function will preserve existing timezone offsets on dates, and otherwise
// will use a regex to deconstruct the `dateString` and use a JS local date constructor.
// `dateString` _must_ take the form of a ISO8601 DateTime string in "extended" form
// (with or without tz) in order to be correctly parsed.
// Ex: "2020-01-01T00:00:00" for 12 AM on January 1, 2020
const dateWithTheRightTimezone = (dateString: string): Date | undefined => {
  if (dateString.endsWith('Z')) {
    return new Date(dateString);
  }

  if (timezoneOffsetRegex.test(dateString)) {
    // There is already a timezone offset.
    return new Date(dateString);
  }

  if (isNaN(new Date(dateString).getTime())) {
    return undefined;
  }

  const [date, time] = dateString.split('T');
  const [year, month, day] = date.split('-').map(x => parseInt(x, 10));
  const [hour, minute, second] = time.split(':').map(x => parseFloat(x));

  const [s, ms] = second.toString().split('.').map(x => parseInt(x, 10));

  return new Date(year, month - 1, day, hour, minute, s, ms || 0);
};

export const validDateOrUndefined = (dateString?: string): Date | undefined => {
  if (isNullOrUndefined(dateString)) {
    return undefined;
  }

  const result = dateWithTheRightTimezone(dateString);

  if (result === undefined || isNaN(result.getTime())) {
    return undefined;
  }

  return result;
};

const isNullOrUndefined = (date: any) => date === undefined || date === null;

